import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
  RequiredFieldsText,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { ToggleField } from "@components/forms";
import { ApplicationId } from "@lib/constants";
import { ConfigurationKeys } from "@services/configuration";

import { useConfigurations, useUpdateConfigurations } from "../vr-settings/api";
import { getConfigurationsUpdated } from "../vr-settings/utils";

import "./program-settings.modal.scss";

export const ProgramSettingsModal = ({ open, organization, onClose }) => {
  const initialConfigurationsRef = useRef();

  const [configurations, setConfigurations] = useState();
  const [isApplyEnabled, setIsApplyEnabled] = useState(false);

  const { loading: loadingConfigurations, getConfigurations } =
    useConfigurations(ApplicationId.DASHBOARD, organization.id, {
      onSuccess: (data) => {
        initialConfigurationsRef.current = data;
        setConfigurations(data);
      },
    });

  const {
    loading: updatingConfiguration,
    success: updatingConfigurationSuccess,
    error: updateConfigurationError,
    resetStatus: resetUpdateConfigurationStatus,
    updateConfiguration,
  } = useUpdateConfigurations(ApplicationId.DASHBOARD, organization.id, {
    onSuccess: () => {
      getConfigurations();
    },
  });

  const onConfigurationChange = (key, value) => {
    setConfigurations((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const onAiCurriculumChange = (value) => {
    setConfigurations((prev) => {
      const next = {
        ...prev,
        [ConfigurationKeys.AI_CURRICULUM_ENABLED]: {
          ...prev[ConfigurationKeys.AI_CURRICULUM_ENABLED],
          value,
        },
      };

      if (value && next[ConfigurationKeys.COURSE_BUILDER_ENABLED]) {
        next[ConfigurationKeys.COURSE_BUILDER_ENABLED] = {
          ...next[ConfigurationKeys.COURSE_BUILDER_ENABLED],
          value: true,
        };
      }

      return next;
    });
  };

  const onApply = async () => {
    // Only send the configurations that have changed.
    const configurationsToUpdate = getConfigurationsUpdated(
      initialConfigurationsRef.current,
      configurations,
    );

    // The BE PUT requires whitelisted_urls even when unchanged.
    if (!configurationsToUpdate["whitelistedUrls"]) {
      configurationsToUpdate["whitelistedUrls"] =
        initialConfigurationsRef.current["whitelistedUrls"];
    }

    await updateConfiguration(configurationsToUpdate);
  };

  useEffect(() => {
    if (open) {
      getConfigurations();
    } else {
      setConfigurations();
      resetUpdateConfigurationStatus();
      initialConfigurationsRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    const configurationToUpdate = getConfigurationsUpdated(
      initialConfigurationsRef.current,
      configurations,
    );
    setIsApplyEnabled(Object.keys(configurationToUpdate).length > 0);
  }, [configurations]);

  useEffect(() => {
    // Close the notification after 1 second
    if (updatingConfigurationSuccess && !loadingConfigurations) {
      const timeoutId = setTimeout(() => {
        resetUpdateConfigurationStatus();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [updatingConfigurationSuccess, loadingConfigurations]);

  return createPortal(
    <Modal
      preventClose={updatingConfiguration}
      modalClassName="program-settings-modal"
      open={open}
      onClose={onClose}
    >
      {updatingConfiguration && <Loader overlay />}
      <ModalHeader title="Program Settings" label={organization.name} />
      <ModalBody>
        {updateConfigurationError && (
          <Notification type={NotificationType.error}>
            There was an error updating the settings. Please try again.
          </Notification>
        )}
        {updatingConfigurationSuccess && (
          <Notification type={NotificationType.success}>
            Settings updated successfully.
          </Notification>
        )}
        <RequiredFieldsText />
        <ToggleField
          label="Course Builder"
          description={
            configurations?.[ConfigurationKeys.COURSE_BUILDER_ENABLED]
              ?.description
          }
          value={
            configurations?.[ConfigurationKeys.COURSE_BUILDER_ENABLED]?.value
          }
          onChange={(value) =>
            onConfigurationChange(
              ConfigurationKeys.COURSE_BUILDER_ENABLED,
              value,
            )
          }
          loading={loadingConfigurations}
          required={true}
          hidden={!configurations?.[ConfigurationKeys.COURSE_BUILDER_ENABLED]}
        />
        <ToggleField
          label="AI Curriculum Assistant"
          description={
            configurations?.[ConfigurationKeys.AI_CURRICULUM_ENABLED]
              ?.description
          }
          value={
            configurations?.[ConfigurationKeys.AI_CURRICULUM_ENABLED]?.value
          }
          onChange={onAiCurriculumChange}
          loading={loadingConfigurations}
          required={true}
          hidden={!configurations?.[ConfigurationKeys.AI_CURRICULUM_ENABLED]}
        />
      </ModalBody>
      <ModalFooter>
        <Button disabled={updatingConfiguration} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={updatingConfiguration || !isApplyEnabled}
          onClick={onApply}
          primary
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>,
    document.body,
  );
};
